import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  bdcApi,
  staffBdcApi,
  type BdcCompanyDataResponse,
  type BdcCompanyDataValues,
} from "../../../api/bdc.api";
import { useAppSelector } from "../../../redux/hooks";
import { vaultKeys } from "../queryKeys";
import { getVaultActorAuthScope } from "../../../utils/vault/vaultActorSession";

interface BdcCompanyDataQueryOptions {
  /** Internal `/folders` read-only mount; uses the session/cookie client. */
  isAdminContext?: boolean;
  enabled?: boolean;
}

export function useBdcCompanyData(
  requirementId?: string,
  options: BdcCompanyDataQueryOptions = {},
) {
  const jwtToken = useAppSelector((state) => state.vault.jwtToken);
  const isAdminContext = options.isAdminContext === true;
  const authScope = isAdminContext ? "staff" : getVaultActorAuthScope(jwtToken);
  const client = isAdminContext ? staffBdcApi : bdcApi;

  return useQuery<BdcCompanyDataResponse>({
    queryKey: vaultKeys.bdcCompanyData(requirementId ?? "", authScope),
    queryFn: ({ signal }) => client.getCompanyData(requirementId!, signal),
    enabled:
      Boolean(requirementId) &&
      (isAdminContext || Boolean(jwtToken)) &&
      (options.enabled ?? true),
    staleTime: 0,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

export function useConfirmBdcCompanyData(folderId?: string) {
  const queryClient = useQueryClient();
  const jwtToken = useAppSelector((state) => state.vault.jwtToken);
  const authScope = getVaultActorAuthScope(jwtToken);

  return useMutation<
    BdcCompanyDataResponse,
    unknown,
    { requirementId: string; values: BdcCompanyDataValues }
  >({
    mutationFn: async ({ requirementId, values }) => {
      const queryKey = vaultKeys.bdcCompanyData(requirementId, authScope);
      await queryClient.cancelQueries({ queryKey, exact: true });
      return bdcApi.confirmCompanyData(requirementId, values);
    },
    onSuccess: async (data, { requirementId }) => {
      const queryKey = vaultKeys.bdcCompanyData(requirementId, authScope);
      // A remounted view may start another GET while the PUT is in flight.
      await queryClient.cancelQueries({ queryKey, exact: true });
      queryClient.setQueryData(queryKey, data);
      if (folderId) {
        void queryClient.invalidateQueries({
          queryKey: vaultKeys.folder(folderId),
        });
      }
    },
  });
}
